import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import type { Store } from '@/redux/store';
import { signIn, signOut } from '@/redux/auth/auth.slice';
import { disconnect } from '@/redux/notification/notification.slice';
import { refreshToken } from '@/api/auth.api';

// 擴展 AxiosRequestConfig 類型
declare module 'axios' {
  export interface AxiosRequestConfig {
    _retry?: boolean;
    skipAuthRefresh?: boolean;
  }
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.map((cb) => cb(token));
  refreshSubscribers = [];
};

const isTokenExpiringSoon = (refreshTokenTime: number | undefined): boolean => {
  if (!refreshTokenTime) return true;
  const currentTime = Date.now();
  const timeDifference = refreshTokenTime - currentTime;
  // 在距離 token 過期的 5 分鐘內時需要重新刷新 token
  return timeDifference <= 5 * 60 * 1000;
};

export const axios = Axios.create();

export const axiosInterceptor = (store: Store) => {
  const requestBefore = async (config: CustomAxiosRequestConfig): Promise<CustomAxiosRequestConfig> => {
    // 如果請求被標記為跳過認證刷新，直接返回
    if (config.skipAuthRefresh) {
      return config;
    }

    config.headers = config.headers || {};

    const state = store.getState();
    const { isLogin, userData, accessToken, refreshTokenTime } = state.auth;

    if (isLogin && userData && accessToken) {
      if (isTokenExpiringSoon(refreshTokenTime)) {
        if (!isRefreshing) {
          console.log('開始刷新 token...');
          isRefreshing = true;

          try {
            // 發送刷新 token 請求時，標記為跳過認證刷新
            const res = await axios.post('/api/auth/refresh', accessToken, { skipAuthRefresh: true });
            const newToken = res.data.data.access_token;
            
            // 更新 store 中的 token 和過期時間
            store.dispatch(
              signIn({
                ...store.getState().auth,
                isLogin: true,
                accessToken: newToken,
                refreshTokenTime: Date.now() + 60 * 60 * 1000 // 設置新的過期時間（1小時）
              })
            );

            onRefreshed(newToken);
            console.log('token 刷新成功');
          } catch (error) {
            console.error('token 刷新失敗', error);
            console.log('準備重定向到登入頁面，當前路徑：', window.location.pathname);
            store.dispatch(signOut());
            store.dispatch(disconnect());
            
            // 重定向到登入頁面
            const currentPath = window.location.pathname;
            if (currentPath !== '/auth/signin') {
              console.log('執行重定向到：', `/auth/signin?path=${currentPath}`);
              window.location.href = `/auth/signin?path=${currentPath}`;
            } else {
              console.log('當前已在登入頁面，不進行重定向');
            }
          } finally {
            isRefreshing = false;
          }
        }

        // 等待 token 刷新完成
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
            resolve(config);
          });
        });
      }

      // 如果 token 未過期，使用當前 token
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  };

  const responseError = async (error: any): Promise<any> => {
    const originRequest = error.config;
    
    // 如果請求已經重試過或被標記為跳過認證刷新，直接拋出錯誤
    if (originRequest._retry || originRequest.skipAuthRefresh) {
      console.log('請求已重試過或被標記為跳過認證刷新，準備重定向到登入頁面');
      handleAuthError();
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      console.log('收到 401 錯誤，檢查是否可以刷新 token');
      originRequest._retry = true;
      const state = store.getState();
      const { accessToken } = state.auth;

      if (!accessToken) {
        console.log('沒有 access token，直接重定向到登入頁面');
        handleAuthError();
        return Promise.reject(error);
      }

      try {
        console.log('嘗試刷新 token...');
        const res = await axios.post('/api/auth/refresh', accessToken, { skipAuthRefresh: true });
        const newToken = res.data.data.access_token;
        
        store.dispatch(
          signIn({
            ...state.auth,
            isLogin: true,
            accessToken: newToken,
            refreshTokenTime: Date.now() + 60 * 60 * 1000
          })
        );

        originRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originRequest);
      } catch (refreshError) {
        console.log('token 刷新失敗，準備重定向到登入頁面', refreshError);
        handleAuthError();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  };

  // 新增處理認證錯誤的函數
  const handleAuthError = () => {
    store.dispatch(signOut());
    store.dispatch(disconnect());
    
    const currentPath = window.location.pathname;
    console.log('處理認證錯誤，當前路徑：', currentPath);
    
    if (currentPath !== '/auth/signin') {
      const redirectUrl = `/auth/signin?path=${encodeURIComponent(currentPath)}`;
      console.log('重定向到：', redirectUrl);
      window.location.href = redirectUrl;
    } else {
      console.log('已在登入頁面，不進行重定向');
    }
  };

  axios.interceptors.request.use(requestBefore);
  axios.interceptors.response.use(undefined, responseError);
};

export async function axiosFetch<T>(axiosConfig: AxiosConfig) {
  const response: AxiosResponse<T> = {
    res: {
      success: false,
      message: '',
      data: {},
    },
  };

  const { axiosInstance, method, url, requestConfig = {} } = axiosConfig;
  try {
    const res = await axiosInstance[method.toLowerCase()](url, requestConfig.data);
    response.res = res.data;
  } catch (err: any) {
    console.log('err: ', err.response);
    response.res = err.response.data;
  } finally {
    return response;
  }
}
